/**
 * US phone checks for the MDI registration flow.
 *
 * Area-code → primary state mapping is derived from the MIT-licensed
 * node-areacodes dataset (nationalnanpa.com–based); see nanp-area-code-metadata.json.
 */
import nanp from "./nanp-area-code-metadata.json";
import { US_STATES } from "./constants";

type NanpRow = {
  type?: string;
  city?: string | null;
  state?: string | null;
  stateCode?: string | null;
};

const US_STATE_BY_LOWER = new Map(
  US_STATES.map((s) => [s.toLowerCase(), s] as const)
);

function canonicalUsStateNameFromNanp(row: NanpRow): string | undefined {
  const raw = row.state;
  if (!raw || typeof raw !== "string") return undefined;
  const key = raw.trim().replace(/\s+/g, " ").toLowerCase();
  return US_STATE_BY_LOWER.get(key);
}

/** 555-0100 … 555-0199 — reserved fictional / test range; still “valid” in libphonenumber. */
export function isReservedNanp5550100Through0199(digits10: string): boolean {
  if (!/^\d{10}$/.test(digits10)) return false;
  const nxx = digits10.slice(3, 6);
  const line = digits10.slice(6);
  if (nxx !== "555") return false;
  const n = parseInt(line, 10);
  return n >= 100 && n <= 199;
}

export type PhoneGeoErrorKey = "testLine" | "tollFree" | "territory" | "stateMismatch";

export function evaluatePhoneAgainstSelectedState(
  digits10: string,
  selectedState: string
): { ok: true } | { ok: false; errorKey: PhoneGeoErrorKey } {
  if (!/^\d{10}$/.test(digits10)) return { ok: true };

  if (isReservedNanp5550100Through0199(digits10)) {
    return { ok: false, errorKey: "testLine" };
  }

  const npa = digits10.slice(0, 3);
  const row = (nanp as Record<string, NanpRow | undefined>)[npa];
  if (!row) return { ok: true };

  if (row.type === "toll-free" || row.type === "eas") {
    return { ok: false, errorKey: "tollFree" };
  }

  const sc = row.stateCode?.toUpperCase() ?? "";
  if (sc && ["VI", "GU", "AS", "MP"].includes(sc)) {
    return { ok: false, errorKey: "territory" };
  }
  if (sc === "DC") {
    return { ok: true };
  }

  const trimmedState = selectedState.trim();
  if (!trimmedState) return { ok: true };

  const canon = canonicalUsStateNameFromNanp(row);
  if (!canon) {
    return { ok: false, errorKey: "territory" };
  }

  if (canon !== trimmedState) {
    return { ok: false, errorKey: "stateMismatch" };
  }

  return { ok: true };
}
